import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Card, Fieldset, Radio, ValidationMessage } from '@digdir/designsystemet-react';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@navikt/aksel-icons';

import styled from '../../../../../styled';
import { ErrorBox } from '../../../../../components/errorBox';
import { VentilasjonsType } from '../../../../../components/registering/utils/registerEnums';
import { ButtonWrapper, MainContentWrapper } from '../../solceller/form/components/styledComponents';
import { FormErrors } from '../../../../../components/form/formErrors';
import { useRegistrationSteps } from '../../../hooks/useRegistrationSteps';
import { RegistrationCommonStep } from '../../../../../utils/navigation';
import { FormBackButton } from '../../../../../screens/registrationV2025/components/formBackButton';

import { BalansertVentilasjonInputs } from './balansertVentilasjonInput';
import { VentilasjonFormData } from './types';
import { useVentilasjonSubmit } from './useVentilasjonSubmit';


interface Props {
  defaultValues?: VentilasjonFormData;
}
const FormCard = styled(Card)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--ds-size-1);
`;

export const VentilasjonsForm: FC<Props> = ({ defaultValues }) => {
  const { t } = useTranslation('', { keyPrefix: 'form' });

  const methods = useForm<VentilasjonFormData>({ defaultValues });
  const { onSubmit, isLoading, updateError } = useVentilasjonSubmit();
  const navigate = useNavigate();
  const { registrationId } = useParams();
  const { previousStep, nextStep } = useRegistrationSteps(RegistrationCommonStep.VENTILATION);

  const ventilasjonstype = methods.watch('ventilasjonType');

  const ventilasjonsTypeRegister = methods.register('ventilasjonType', {
    required: t('ventilasjon.ventilasjonSelection.error')
  });

  const onFormSubmit = (data: VentilasjonFormData) => onSubmit({ data, onSuccess: () => navigate(`../${nextStep}`) });
  const navigateBack = () => navigate(`../${previousStep}`);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onFormSubmit)} noValidate>
        <MainContentWrapper>
          <FormCard>
            <Fieldset disabled={isLoading}>
              <Fieldset.Legend>{t('ventilasjon.ventilasjonSelection.heading')}</Fieldset.Legend>
              <Radio
                {...ventilasjonsTypeRegister}
                label={t(`ventilasjon.options.${VentilasjonsType.Naturlig}.label`)}
                value={VentilasjonsType.Naturlig}
                description={
                  ventilasjonstype === VentilasjonsType.Naturlig &&
                  t(`ventilasjon.options.${VentilasjonsType.Naturlig}.description`)
                }
              />
              <Radio
                {...ventilasjonsTypeRegister}
                label={t(`ventilasjon.options.${VentilasjonsType.PeriodiskAvtrekk}.label`)}
                value={VentilasjonsType.PeriodiskAvtrekk}
                description={
                  ventilasjonstype === VentilasjonsType.PeriodiskAvtrekk &&
                  t(`ventilasjon.options.${VentilasjonsType.PeriodiskAvtrekk}.description`)
                }
              />
              <Radio
                {...ventilasjonsTypeRegister}
                label={t(`ventilasjon.options.${VentilasjonsType.MekaniskAvtrekk}.label`)}
                value={VentilasjonsType.MekaniskAvtrekk}
                description={
                  ventilasjonstype === VentilasjonsType.MekaniskAvtrekk &&
                  t(`ventilasjon.options.${VentilasjonsType.MekaniskAvtrekk}.description`)
                }
              />
              <Radio
                {...ventilasjonsTypeRegister}
                label={t(`ventilasjon.options.${VentilasjonsType.Balansert}.label`)}
                value={VentilasjonsType.Balansert}
              />
              {
                // Show additional input fields when "Balansert" is selected
                ventilasjonstype === VentilasjonsType.Balansert && <BalansertVentilasjonInputs />
              }

              <Radio
                {...methods.register('ventilasjonType', {
                  required: t('ventilasjon.ventilasjonSelection.error')
                })}
                label={t(`ventilasjon.options.${VentilasjonsType.Ukjent}.label`)}
                value={VentilasjonsType.Ukjent}
              />
              {methods.formState.errors.ventilasjonType && (
                <ValidationMessage data-color="danger">
                  {methods.formState.errors.ventilasjonType.message}
                </ValidationMessage>
              )}
            </Fieldset>
          </FormCard>
          <FormErrors errors={methods.formState.errors} />
          <ErrorBox error={updateError} registrationId={registrationId} />
          <ButtonWrapper>
            <FormBackButton<VentilasjonFormData> isLoading={isLoading} onSuccess={navigateBack} onSubmit={onSubmit} />

            <Button loading={isLoading} type="submit">
              {t('common.next')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </MainContentWrapper>
      </form>
    </FormProvider>
  );
};
